import React, { useCallback, useEffect, useState } from 'react';
import { Upload, message, Image, Spin, Form } from 'antd';
import type { RcFile } from 'antd/es/upload';

import { FormItemProps } from 'antd/lib/form';
import { FieldInputProps, FormikTouched, FormikErrors } from 'formik';

import { useAppDispatch } from 'src/stores';
import { uploadFileAction } from 'src/stores/screens/common/common.action';
import { UploadImageStyled } from './UploadImageStyle';
import { CloseOutlined } from '@ant-design/icons';
import { Player } from 'video-react';

import 'video-react/dist/video-react.css';

export type UploadImageProps = {
  file?: string;
  fileType?: string;
  setFieldValue: (field: string, value: any) => any;
  label?: string;
  touched?: boolean | FormikTouched<any> | Array<FormikTouched<any>>;
  error?: string | string[] | FormikErrors<any> | Array<FormikErrors<any>>;
  field: FieldInputProps<any>;
  disabled?: boolean;
  uploadVideo?: boolean;
} & Omit<FormItemProps, 'children'>;

const UploadImage: React.FC<UploadImageProps> = ({
  file,
  fileType,
  setFieldValue,
  label,
  touched,
  error,
  field,
  disabled,
  uploadVideo,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const [previewFile, setPreviewFile] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [type, setType] = useState<string>('');

  useEffect(() => {
    if (file) {
      setPreviewFile(file);
    }
    return () => {
      setPreviewFile('');
    };
  }, [file]);

  useEffect(() => {
    if (fileType?.includes('video')) {
      setType('video');
    }
    return () => {
      setType('image');
    };
  }, [fileType]);

  const beforeUpload = (file: RcFile) => {
    const isJpgOrPng =
      file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/jpg' ||
      file.type === 'image/gif';
    const isVideo = file.type.includes('video');
    if (isVideo) {
      setType('video');
    } else {
      setType('image');
    }

    if (!isJpgOrPng && !uploadVideo) {
      void message.error('You can only upload jpeg/jpg/png/gif files!');
      return false;
    }
    if (!isJpgOrPng && !isVideo) {
      void message.error('You can only upload jpeg/jpg/png/gif/mp4 files!');
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (isJpgOrPng && !isLt2M) {
      void message.error('The file must be smaller than 2MB!');
      return false;
    }
    if (isVideo && uploadVideo && !isLt10M) {
      void message.error('The file must be smaller than 10MB!');
      return false;
    }
    return (isJpgOrPng && isLt2M) || (isVideo && isLt10M && !!uploadVideo);
  };

  const customRequest = async ({ file, onSuccess, onError }: any) => {
    const formData = new FormData();
    formData.append('file', file);
    setIsLoading(true);
    const result: any = await dispatch(
      uploadFileAction({ data: formData, callback: () => {} })
    );
    setIsLoading(false);
    if (result?.error) {
      onError(result?.error);
      void message.error('Image upload failed');
    } else {
      onSuccess(result?.payload?.fileUrl);
      setType(file.type.includes('video') ? 'video' : 'image');
      setFieldValue(field.name, result?.payload?.fileUrl);
      setFieldValue('fileType', result?.payload?.fileType);
    }
  };

  delete field.value;

  const RenderPreview = useCallback(() => {
    if (isLoading) {
      return (
        <div className="loading">
          <Spin />
        </div>
      );
    }

    if (previewFile) {
      return type === 'video' ? (
        <div className="video-wrapper">
          <Player videoHeight={200}>
            <source src={previewFile} />
          </Player>
          <CloseOutlined
            className="btn-remove-img"
            onClick={(e) => {
              e.stopPropagation();
              setFieldValue(field.name, '');
              setFieldValue('fileType', '');
            }}
          />
        </div>
      ) : (
        <>
          <Image
            width={200}
            height={200}
            src={previewFile}
            style={{ cursor: 'pointer' }}
            alt="uploaded-image"
            preview={false}
          />
          <CloseOutlined
            className="btn-remove-img"
            onClick={(e) => {
              e.stopPropagation();
              setFieldValue(field.name, '');
              setFieldValue('fileType', '');
            }}
          />
        </>
      );
    }

    if (!previewFile) {
      return <div className="upload-wrapper">Select an file to upload</div>;
    }
  }, [file, previewFile, type]);

  return (
    <UploadImageStyled>
      <Form.Item
        colon={false}
        label={label}
        name="thumbnail"
        validateStatus={error && touched ? 'error' : ''}
        help={(touched && error) as string}
        {...rest}
      >
        <Upload
          customRequest={customRequest}
          disabled={disabled ?? isLoading ?? false}
          name="image"
          showUploadList={false}
          accept={
            uploadVideo ? '.jpeg,.jpg,.png,.gif,.mp4' : '.jpeg,.jpg,.png,.gif'
          }
          beforeUpload={beforeUpload}
        >
          {RenderPreview()}
        </Upload>
      </Form.Item>
    </UploadImageStyled>
  );
};

export default UploadImage;
